import { Matcher } from '../Utils/Matcher';
import { PerformanceMonitor } from '../../Utils/PerformanceMonitor';
/**
 * 实体系统的基类
 *
 * 用于处理一组符合特定条件的实体。系统是ECS架构中的逻辑处理单元，
 * 负责对拥有特定组件组合的实体执行业务逻辑。
 *
 * @example
 * ```typescript
 * class MovementSystem extends EntitySystem {
 *     constructor() {
 *         super(Matcher.empty().all(Transform, Velocity));
 *     }
 *
 *     protected process(entities: Entity[]): void {
 *         for (const entity of entities) {
 *             const transform = entity.getComponent(Transform);
 *             const velocity = entity.getComponent(Velocity);
 *             transform.position.add(velocity.value);
 *         }
 *     }
 * }
 * ```
 */
export class EntitySystem {
    /**
     * 获取系统处理的实体列表
     */
    get entities() {
        return this._entities;
    }
    /**
     * 获取系统的更新时序
     */
    get updateOrder() {
        return this._updateOrder;
    }
    set updateOrder(value) {
        this.setUpdateOrder(value);
    }
    /**
     * 获取系统的启用状态
     */
    get enabled() {
        return this._enabled;
    }
    /**
     * 设置系统的启用状态
     */
    set enabled(value) {
        this._enabled = value;
    }
    /**
     * 获取系统名称
     */
    get systemName() {
        return this._systemName;
    }
    constructor(matcher) {
        this._entities = [];
        this._updateOrder = 0;
        this._enabled = true;
        this._performanceMonitor = PerformanceMonitor.instance;
        this._matcher = matcher ? matcher : Matcher.empty();
        this._systemName = this.constructor.name;
        this.initialize();
    }
    /**
     * 这个系统所属的场景
     */
    get scene() {
        return this._scene;
    }
    set scene(value) {
        this._scene = value;
        this._entities = [];
    }
    /**
     * 获取实体匹配器
     */
    get matcher() {
        return this._matcher;
    }
    /**
     * 设置更新时序
     * @param order 更新时序
     */
    setUpdateOrder(order) {
        this._updateOrder = order;
        this.scene.entityProcessors.setDirty();
    }
    /**
     * 系统初始化
     *
     * 在系统创建时调用，子类可以重写此方法进行初始化操作。
     */
    initialize() {
        // 子类可以重写此方法
    }
    /**
     * 当实体的组件发生变化时调用
     *
     * 检查实体是否仍然符合系统的匹配条件，并相应地添加或移除实体。
     *
     * @param entity 发生变化的实体
     */
    onChanged(entity) {
        const contains = this._entities.includes(entity);
        const interest = this._matcher.isInterestedEntity(entity);
        if (interest && !contains) {
            this.add(entity);
        }
        else if (!interest && contains) {
            this.remove(entity);
        }
    }
    /**
     * 添加实体到系统
     *
     * @param entity 要添加的实体
     */
    add(entity) {
        if (!this._entities.includes(entity)) {
            this._entities.push(entity);
            this.onAdded(entity);
        }
    }
    /**
     * 当实体被添加到系统时调用
     *
     * 子类可以重写此方法来处理实体添加事件。
     *
     * @param entity 被添加的实体
     */
    onAdded(entity) {
        // 子类可以重写此方法
    }
    /**
     * 从系统中移除实体
     *
     * @param entity 要移除的实体
     */
    remove(entity) {
        const index = this._entities.indexOf(entity);
        if (index !== -1) {
            this._entities.splice(index, 1);
            this.onRemoved(entity);
        }
    }
    /**
     * 当实体从系统中移除时调用
     *
     * 子类可以重写此方法来处理实体移除事件。
     *
     * @param entity 被移除的实体
     */
    onRemoved(entity) {
        // 子类可以重写此方法
    }
    /**
     * 更新系统
     *
     * 在每帧调用，处理系统的主要逻辑。
     */
    update() {
        if (!this._enabled || !this.checkProcessing()) {
            return;
        }
        const startTime = this._performanceMonitor.startMonitoring(this._systemName);
        try {
            this.begin();
            this.process(this._entities);
        }
        finally {
            this._performanceMonitor.endMonitoring(this._systemName, startTime, this._entities.length);
        }
    }
    /**
     * 后期更新系统
     *
     * 在所有系统的update方法执行完毕后调用。
     */
    lateUpdate() {
        if (!this._enabled || !this.checkProcessing()) {
            return;
        }
        const startTime = this._performanceMonitor.startMonitoring(`${this._systemName}_Late`);
        try {
            this.lateProcess(this._entities);
            this.end();
        }
        finally {
            this._performanceMonitor.endMonitoring(`${this._systemName}_Late`, startTime, this._entities.length);
        }
    }
    /**
     * 在系统处理开始前调用
     *
     * 子类可以重写此方法进行预处理操作。
     */
    begin() {
        // 子类可以重写此方法
    }
    /**
     * 处理实体列表
     *
     * 系统的核心逻辑，子类必须实现此方法来定义具体的处理逻辑。
     *
     * @param entities 要处理的实体列表
     */
    process(entities) {
        // 子类必须实现此方法
    }
    /**
     * 后期处理实体列表
     *
     * 在主要处理逻辑之后执行，子类可以重写此方法。
     *
     * @param entities 要处理的实体列表
     */
    lateProcess(entities) {
        // 子类可以重写此方法
    }
    /**
     * 系统处理完毕后调用
     *
     * 子类可以重写此方法进行后处理操作。
     */
    end() {
        // 子类可以重写此方法
    }
    /**
     * 检查系统是否需要处理
     *
     * 在启用系统时有用，但仅偶尔需要处理。
     * 这只影响处理，不影响事件或订阅列表。
     *
     * @returns 如果系统应该处理，则为true，如果不处理则为false
     */
    checkProcessing() {
        return true;
    }
    /**
     * 获取系统的性能数据
     *
     * @returns 性能数据或undefined
     */
    getPerformanceData() {
        return this._performanceMonitor.getSystemData(this._systemName);
    }
    /**
     * 获取系统的性能统计
     *
     * @returns 性能统计或undefined
     */
    getPerformanceStats() {
        return this._performanceMonitor.getSystemStats(this._systemName);
    }
    /**
     * 重置系统的性能数据
     */
    resetPerformanceData() {
        this._performanceMonitor.resetSystem(this._systemName);
    }
    /**
     * 获取系统信息的字符串表示
     *
     * @returns 系统信息字符串
     */
    toString() {
        const entityCount = this._entities.length;
        const perfData = this.getPerformanceData();
        const perfInfo = perfData ? ` (${perfData.executionTime.toFixed(2)}ms)` : '';
        return `${this._systemName}[${entityCount} entities]${perfInfo}`;
    }
}
//# sourceMappingURL=EntitySystem.js.map